import React, { useEffect, useState } from "react";
import { Button } from "reactstrap";
import { Tag, message } from "antd";
import axios from "axios";
import baseURL from "config";
import { errorMessage } from "helpers/ApiHelper";
import ListSearch from "components/ListSearch/ListSearch";
import { useTextFilter } from "helpers/textFilter";
import FAQForm, { categoryLabels } from "./FAQForm";
import { swapPatches } from "./faqOrder";

const VIEWS = {
  LIST: "LIST",
  ADD_NEW: "ADD_NEW",
  EDIT: "EDIT",
};

const questionCategories = {
  Booking: "Booking",
  Pricing: "Pricing",
  Services: "Services",
  Profiles: "Profiles",
};

const HeaderButton = (props) => {
  return (
    <Button
      {...props}
      color={props?.color || "success"}
      size={props?.size || "md"}
      style={{
        maxWidth: "150px",
        minHeight: "44px",
      }}
    >
      {props.children}
    </Button>
  );
};

// `active` only styles the button; spreading it onto <button> made React warn
// about a non-boolean DOM attribute.
const Category = ({ active, ...props }) => {
  return (
    <button
      style={{
        minHeight: "44px",
        borderRadius: "16px",
        border: active ? "1px solid black" : "1px solid lightgray",
        minWidth: "100px",
        backgroundColor: active ? "#ADD8E6" : "white",
      }}
      aria-pressed={Boolean(active)}
      {...props}
    />
  );
};

const Arrows = ({ onIncrement, onDecrement, disabled }) => {
  return (
    <div style={{ display: "flex", gap: "4px" }}>
      <button
        disabled={disabled}
        onClick={onIncrement}
        aria-label="Премести нагоре"
        style={{
          width: "44px",
          height: "44px",
          borderRadius: "8px",
        }}
      >
        ↑
      </button>
      <button
        disabled={disabled}
        onClick={onDecrement}
        aria-label="Премести надолу"
        style={{
          width: "44px",
          height: "44px",
          borderRadius: "8px",
        }}
      >
        ↓
      </button>
    </div>
  );
};

/** The list falls back to BG — imported questions have no EN yet (DAB-858). */
const displayTitle = (item) => item?.question?.en || item?.question?.bg || "";

const faqSearchFields = (item) => [item?.question?.bg, item?.question?.en, item?.answer?.bg, item?.answer?.en];

export default function FAQ() {
  const [view, setView] = useState(VIEWS.LIST);
  const [activeCategory, setActiveCategory] = useState(
    questionCategories.Booking
  );
  const [selectedItem, setSelectedItem] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const getFAQS = async () => {
    try {
      const questions = await axios.get(`${baseURL}/api/v1/faq`);
      setFaqs(questions?.data);
    } catch (e) {
      message.error(`Неуспешно зареждане на въпросите: ${errorMessage(e)}`);
    }
  };

  useEffect(() => {
    getFAQS();
  }, []);

  const editItemView = (item) => {
    setView(VIEWS.EDIT);
    setSelectedItem(item);
  };

  const closeEditView = () => {
    setView(VIEWS.LIST);
    setSelectedItem(null);
  };

  const deleteItem = async (item) => {
    if (
      window.confirm(
        `Ще изтриеш следния въпрос: ${displayTitle(item)}`
      )
    ) {
      try {
        await axios.delete(`${baseURL}/api/v1/faq/${item._id}`);
        message.success("Изтрито");
        const questions = await axios.get(`${baseURL}/api/v1/faq`);
        setFaqs(questions?.data);
      } catch (e) {
        message.error(`Неуспешно изтриване: ${errorMessage(e)}`);
      }
    }
  };

  const reorder = async (item, direction) => {
    const categoryRows = faqs.filter(
      (question) => question.category === item.category
    );
    const patches = swapPatches(
      categoryRows,
      item._id,
      direction === "increment" ? "up" : "down"
    );
    if (!patches.length) return;

    try {
      // The two patches target distinct rows, so they can run together.
      await Promise.all(
        patches.map((patch) =>
          axios.patch(`${baseURL}/api/v1/faq/${patch.id}`, {
            order: patch.order,
          })
        )
      );
      const questions = await axios.get(`${baseURL}/api/v1/faq`);
      setFaqs(questions?.data);
    } catch (e) {
      message.error(`Неуспешна промяна на подредбата: ${errorMessage(e)}`);
      const questions = await axios.get(`${baseURL}/api/v1/faq`);
      setFaqs(questions?.data);
    }
  };

  const submitNewForm = async (formData) => {
    try {
      // No `order`: the API appends new questions to the end of their
      // category (max+1), so every writer gets the same behaviour.
      await axios.post(`${baseURL}/api/v1/faq`, formData);
      message.success("Запазено");
      const questions = await axios.get(`${baseURL}/api/v1/faq`);
      setFaqs(questions?.data);
      setView(VIEWS.LIST);
      setActiveCategory(formData.category);
    } catch (e) {
      message.error(`Неуспешно запазване: ${errorMessage(e)}`);
    }
  };

  const editForm = async (formData) => {
    try {
      await axios.patch(`${baseURL}/api/v1/faq/${selectedItem._id}`, formData);
      message.success("Запазено");
      const questions = await axios.get(`${baseURL}/api/v1/faq`);
      setFaqs(questions?.data);
      // Cleared so the next "Add new" starts fresh instead of inheriting
      // this item's order (DAB-858).
      closeEditView();
      setActiveCategory(formData.category);
    } catch (e) {
      message.error(`Неуспешно запазване: ${errorMessage(e)}`);
    }
  };

  const isSearchActive = Boolean(searchQuery.trim());
  const visibleFaqs = useTextFilter(
    faqs.filter((item) => item.category === activeCategory),
    faqSearchFields,
    searchQuery,
  );

  return (
    <div style={{ padding: "10px" }}>
      {view === VIEWS.LIST && (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <HeaderButton onClick={() => setView(VIEWS.ADD_NEW)}>
            Добави въпрос
          </HeaderButton>
          <ListSearch
            value={searchQuery}
            onSearch={setSearchQuery}
            placeholder="Търсене по въпрос или отговор"
          />
          {isSearchActive && (
            <span className="text-muted">
              Изчистете търсенето, за да пренареждате.
            </span>
          )}
          <div style={{ margin: "0 auto", display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <Category
              active={activeCategory === questionCategories.Booking}
              onClick={() => setActiveCategory(questionCategories.Booking)}
            >
              {categoryLabels.Booking}
            </Category>
            <Category
              active={activeCategory === questionCategories.Pricing}
              onClick={() => setActiveCategory(questionCategories.Pricing)}
            >
              {categoryLabels.Pricing}
            </Category>
            <Category
              active={activeCategory === questionCategories.Services}
              onClick={() => setActiveCategory(questionCategories.Services)}
            >
              {categoryLabels.Services}
            </Category>
            <Category
              active={activeCategory === questionCategories.Profiles}
              onClick={() => setActiveCategory(questionCategories.Profiles)}
            >
              {categoryLabels.Profiles}
            </Category>
          </div>
          {visibleFaqs.map((item) => (
              <div
                key={item?._id}
                style={{
                  padding: "20px 0",
                  borderBottom: "1px solid black",
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <h5 style={{ flex: 1, minWidth: "150px", margin: 0 }}>
                  {displayTitle(item)}
                </h5>
                {item.showOnHomepage && <Tag color="blue">На началната страница</Tag>}
                <Arrows
                  onIncrement={() => reorder(item, "increment")}
                  onDecrement={() => reorder(item, "decrement")}
                  disabled={isSearchActive}
                />
                <Button
                  onClick={() => editItemView(item)}
                  color="primary"
                  style={{ minHeight: "44px" }}
                >
                  редактирай
                </Button>
                <Button
                  onClick={() => deleteItem(item)}
                  color="danger"
                  style={{ minHeight: "44px" }}
                >
                  изтрий
                </Button>
              </div>
            ))}
        </div>
      )}
      {view === VIEWS.ADD_NEW && (
        <div>
          <HeaderButton onClick={() => setView(VIEWS.LIST)} color="link">
            {"< Назад"}
          </HeaderButton>
          <h5 style={{marginTop: '10px'}}>
            <b>Добави нов въпрос</b>
          </h5>
          <FAQForm onSubmit={submitNewForm} activeCategory={activeCategory} />
        </div>
      )}
      {view === VIEWS.EDIT && (
        <div>
          <HeaderButton
            color="link"
            onClick={closeEditView}
          >
            {"< Назад"}
          </HeaderButton>
          <h5 style={{marginTop: '10px'}}>
            <b>Редактиране на въпрос:</b> <u>{displayTitle(selectedItem)}</u>
          </h5>
          <FAQForm item={selectedItem} onSubmit={editForm} />
        </div>
      )}
    </div>
  );
}
